/**
 * Minimální generátor QR kódů (byte mode, úrovně L/M, verze 1–10).
 * Stačí na odkazy na karty včelstev, bez externí knihovny.
 */

type Level = 'L' | 'M';

// [EC kódová slova na blok, bloky sk.1, data sk.1, bloky sk.2, data sk.2]
const EC_TABLE: Record<Level, number[][]> = {
  L: [
    [7, 1, 19, 0, 0], [10, 1, 34, 0, 0], [15, 1, 55, 0, 0], [20, 1, 80, 0, 0], [26, 1, 108, 0, 0],
    [18, 2, 68, 0, 0], [20, 2, 78, 0, 0], [24, 2, 97, 0, 0], [30, 2, 116, 0, 0], [18, 2, 68, 2, 69],
  ],
  M: [
    [10, 1, 16, 0, 0], [16, 1, 28, 0, 0], [26, 1, 44, 0, 0], [18, 2, 32, 0, 0], [24, 2, 43, 0, 0],
    [16, 4, 27, 0, 0], [18, 4, 31, 0, 0], [22, 2, 38, 2, 39], [22, 3, 36, 2, 37], [26, 4, 43, 1, 44],
  ],
};

const ALIGN: number[][] = [
  [], [6, 18], [6, 22], [6, 26], [6, 30], [6, 34], [6, 22, 38], [6, 24, 42], [6, 26, 46], [6, 28, 50],
];

const LEVEL_BITS: Record<Level, number> = { L: 1, M: 0 };

// --- Galoisovo těleso GF(256) -------------------------------------------------
const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);
{
  let x = 1;
  for (let i = 0; i < 255; i++) {
    EXP[i] = x;
    LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i++) EXP[i] = EXP[i - 255];
}
const mul = (a: number, b: number) => (a === 0 || b === 0 ? 0 : EXP[LOG[a] + LOG[b]]);

function rsGenerator(degree: number): number[] {
  let g = [1];
  for (let i = 0; i < degree; i++) {
    const next = new Array(g.length + 1).fill(0);
    for (let j = 0; j < g.length; j++) {
      next[j] ^= g[j];
      next[j + 1] ^= mul(g[j], EXP[i]);
    }
    g = next;
  }
  return g;
}

function rsRemainder(data: number[], degree: number): number[] {
  const gen = rsGenerator(degree);
  const res = new Array(degree).fill(0);
  for (const b of data) {
    const factor = b ^ res.shift()!;
    res.push(0);
    for (let i = 0; i < degree; i++) res[i] ^= mul(gen[i + 1], factor);
  }
  return res;
}

// --- Kódování dat ---------------------------------------------------------------

function utf8(text: string): number[] {
  return Array.from(new TextEncoder().encode(text));
}

function dataCapacity(version: number, level: Level): number {
  const [, b1, d1, b2, d2] = EC_TABLE[level][version - 1];
  return b1 * d1 + b2 * d2;
}

function encodeCodewords(bytes: number[], version: number, level: Level): number[] {
  const capacity = dataCapacity(version, level);
  const bits: number[] = [];
  const push = (val: number, len: number) => {
    for (let i = len - 1; i >= 0; i--) bits.push((val >>> i) & 1);
  };
  push(0b0100, 4); // byte mode
  push(bytes.length, version < 10 ? 8 : 16);
  for (const b of bytes) push(b, 8);
  push(0, Math.min(4, capacity * 8 - bits.length));
  while (bits.length % 8) bits.push(0);
  const out: number[] = [];
  for (let i = 0; i < bits.length; i += 8) {
    out.push(bits.slice(i, i + 8).reduce((a, b) => (a << 1) | b, 0));
  }
  for (let pad = 0xec; out.length < capacity; pad ^= 0xec ^ 0x11) out.push(pad);

  // rozdělení do bloků, EC, prokládání
  const [ec, b1, d1, b2, d2] = EC_TABLE[level][version - 1];
  const blocks: number[][] = [];
  let k = 0;
  for (let i = 0; i < b1; i++) blocks.push(out.slice(k, (k += d1)));
  for (let i = 0; i < b2; i++) blocks.push(out.slice(k, (k += d2)));
  const ecBlocks = blocks.map((b) => rsRemainder(b, ec));
  const result: number[] = [];
  const maxD = Math.max(d1, d2);
  for (let i = 0; i < maxD; i++) for (const b of blocks) if (i < b.length) result.push(b[i]);
  for (let i = 0; i < ec; i++) for (const b of ecBlocks) result.push(b[i]);
  return result;
}

// --- Matice -------------------------------------------------------------------

function bch(value: number, poly: number, polyDeg: number): number {
  let v = value << polyDeg;
  for (let i = 31 - Math.clz32(v); i >= polyDeg; i--) if ((v >>> i) & 1) v ^= poly << (i - polyDeg);
  return (value << polyDeg) | v;
}

function buildMatrix(codewords: number[], version: number, level: Level, mask: number): boolean[][] {
  const size = 17 + version * 4;
  const m: (boolean | null)[][] = Array.from({ length: size }, () => new Array(size).fill(null));
  const reserved: boolean[][] = Array.from({ length: size }, () => new Array(size).fill(false));
  const set = (r: number, c: number, v: boolean) => {
    m[r][c] = v;
    reserved[r][c] = true;
  };

  const finder = (r0: number, c0: number) => {
    for (let r = -1; r <= 7; r++)
      for (let c = -1; c <= 7; c++) {
        const rr = r0 + r, cc = c0 + c;
        if (rr < 0 || cc < 0 || rr >= size || cc >= size) continue;
        const on = r >= 0 && r <= 6 && c >= 0 && c <= 6 &&
          (r === 0 || r === 6 || c === 0 || c === 6 || (r >= 2 && r <= 4 && c >= 2 && c <= 4));
        set(rr, cc, on);
      }
  };
  finder(0, 0);
  finder(0, size - 7);
  finder(size - 7, 0);

  for (let i = 8; i < size - 8; i++) {
    set(6, i, i % 2 === 0);
    set(i, 6, i % 2 === 0);
  }

  const al = ALIGN[version - 1];
  const last = al[al.length - 1];
  for (const r of al)
    for (const c of al) {
      // vynechat jen tři rohy s vyhledávacími vzory
      if ((r === 6 && c === 6) || (r === 6 && c === last) || (r === last && c === 6)) continue;
      for (let dr = -2; dr <= 2; dr++)
        for (let dc = -2; dc <= 2; dc++)
          set(r + dr, c + dc, Math.max(Math.abs(dr), Math.abs(dc)) !== 1);
    }

  // formát (dočasně rezervovat) + tmavý modul
  for (let i = 0; i < 9; i++) {
    if (!reserved[8][i]) set(8, i, false);
    if (!reserved[i][8]) set(i, 8, false);
  }
  for (let i = 0; i < 8; i++) {
    set(8, size - 1 - i, false);
    set(size - 1 - i, 8, false);
  }
  set(size - 8, 8, true);

  if (version >= 7) {
    const v = bch(version, 0x1f25, 12);
    for (let i = 0; i < 18; i++) {
      const bit = ((v >>> i) & 1) === 1;
      const a = Math.floor(i / 3), b = (i % 3) + size - 11;
      set(a, b, bit);
      set(b, a, bit);
    }
  }

  // data
  const bits: number[] = [];
  for (const cw of codewords) for (let i = 7; i >= 0; i--) bits.push((cw >>> i) & 1);
  let idx = 0;
  let upward = true;
  for (let col = size - 1; col > 0; col -= 2) {
    if (col === 6) col--;
    for (let i = 0; i < size; i++) {
      const row = upward ? size - 1 - i : i;
      for (let dc = 0; dc < 2; dc++) {
        const c = col - dc;
        if (reserved[row][c]) continue;
        let bit = idx < bits.length ? bits[idx] === 1 : false;
        idx++;
        if (maskFn(mask, row, c)) bit = !bit;
        m[row][c] = bit;
      }
    }
    upward = !upward;
  }

  // formátová informace
  const fmt = bch((LEVEL_BITS[level] << 3) | mask, 0x537, 10) ^ 0x5412;
  const fb = (i: number) => ((fmt >>> i) & 1) === 1;
  // první kopie kolem levého horního vyhledávače
  for (let i = 0; i <= 5; i++) m[i][8] = fb(i);
  m[7][8] = fb(6);
  m[8][8] = fb(7);
  m[8][7] = fb(8);
  for (let i = 9; i < 15; i++) m[8][14 - i] = fb(i);
  // druhá kopie
  for (let i = 0; i < 8; i++) m[8][size - 1 - i] = fb(i);
  for (let i = 8; i < 15; i++) m[size - 15 + i][8] = fb(i);
  m[size - 8][8] = true;
  return m as boolean[][];
}

function maskFn(mask: number, r: number, c: number): boolean {
  switch (mask) {
    case 0: return (r + c) % 2 === 0;
    case 1: return r % 2 === 0;
    case 2: return c % 3 === 0;
    case 3: return (r + c) % 3 === 0;
    case 4: return (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0;
    case 5: return ((r * c) % 2) + ((r * c) % 3) === 0;
    case 6: return (((r * c) % 2) + ((r * c) % 3)) % 2 === 0;
    default: return (((r + c) % 2) + ((r * c) % 3)) % 2 === 0;
  }
}

function penalty(m: boolean[][]): number {
  const n = m.length;
  let p = 0;
  for (let pass = 0; pass < 2; pass++) {
    for (let i = 0; i < n; i++) {
      let run = 1;
      const line: boolean[] = [];
      for (let j = 0; j < n; j++) line.push(pass === 0 ? m[i][j] : m[j][i]);
      for (let j = 1; j < n; j++) {
        if (line[j] === line[j - 1]) run++;
        else {
          if (run >= 5) p += run - 2;
          run = 1;
        }
      }
      if (run >= 5) p += run - 2;
      const s = line.map((b) => (b ? '1' : '0')).join('');
      for (const pat of ['10111010000', '00001011101']) {
        for (let k = s.indexOf(pat); k !== -1; k = s.indexOf(pat, k + 1)) p += 40;
      }
    }
  }
  for (let i = 0; i < n - 1; i++)
    for (let j = 0; j < n - 1; j++) {
      const v = m[i][j];
      if (v === m[i + 1][j] && v === m[i][j + 1] && v === m[i + 1][j + 1]) p += 3;
    }
  const dark = m.flat().filter(Boolean).length;
  p += Math.floor(Math.abs((dark * 100) / (n * n) - 50) / 5) * 10;
  return p;
}

/** Vrátí matici modulů (true = tmavý). */
export function qrMatrix(text: string, level: Level = 'M', forceMask?: number): boolean[][] {
  const bytes = utf8(text);
  let version = 1;
  while (version <= 10) {
    const header = 4 + (version < 10 ? 8 : 16);
    if (header + bytes.length * 8 <= dataCapacity(version, level) * 8) break;
    version++;
  }
  if (version > 10) throw new Error('Text je pro QR kód příliš dlouhý.');
  const codewords = encodeCodewords(bytes, version, level);
  let best: boolean[][] | null = null;
  let bestScore = Infinity;
  for (let mask = 0; mask < 8; mask++) {
    if (forceMask != null && mask !== forceMask) continue;
    const m = buildMatrix(codewords, version, level, mask);
    const score = penalty(m);
    if (score < bestScore) {
      bestScore = score;
      best = m;
    }
  }
  return best!;
}

/** SVG řetězec s okrajem 4 moduly. */
export function qrSvg(text: string, level: Level = 'M'): string {
  const m = qrMatrix(text, level);
  const n = m.length + 8;
  let d = '';
  m.forEach((row, r) =>
    row.forEach((on, c) => {
      if (on) d += `M${c + 4} ${r + 4}h1v1h-1z`;
    }),
  );
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${n} ${n}" shape-rendering="crispEdges"><rect width="${n}" height="${n}" fill="#fff"/><path d="${d}" fill="#000"/></svg>`;
}
